import { memo } from 'react';

import { usePlayerStore } from '@air/component-player';

import {
  PERCENTAGE_MULTIPLIER,
  PLAYBACK_PROGRESS_MIN_WIDTH_PX,
} from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/constants';

type SelectedCommentRangePlaybackProgressProps = {
  endTime: number;
  startTime: number;
};

/**
 * Blue playback progress fill rendered inside the selected-comment range pill.
 * The width is a fraction of the comment range (not the whole track): 0% at the
 * range start, 100% at the range end. Subscribes to the per-frame currentTime
 * here so the parent seekbar does not re-render on every frame.
 */
export const SelectedCommentRangePlaybackProgress = memo(
  ({ endTime, startTime }: SelectedCommentRangePlaybackProgressProps) => {
    const currentTime = usePlayerStore((state) => state.currentTime);

    const rangeStartTime = Math.min(startTime, endTime);
    const rangeEndTime = Math.max(startTime, endTime);
    const rangeDuration = rangeEndTime - rangeStartTime;

    if (rangeDuration <= 0 || currentTime < rangeStartTime) {
      return null;
    }

    const clampedTime = Math.min(currentTime, rangeEndTime);
    const progressFraction = (clampedTime - rangeStartTime) / rangeDuration;

    return (
      <div
        className="h-full rounded-full ads:bg-primary bg-blue-9"
        data-testid="SELECTED_COMMENT_RANGE_PLAYBACK_PROGRESS"
        style={{
          minWidth: `${PLAYBACK_PROGRESS_MIN_WIDTH_PX}px`,
          width: `${progressFraction * PERCENTAGE_MULTIPLIER}%`,
        }}
      />
    );
  },
);

SelectedCommentRangePlaybackProgress.displayName = 'SelectedCommentRangePlaybackProgress';
